import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { DropdownMenu } from "components/inputs/DropdownMenu"
import { InputField } from "components/inputs/InputField"
import { TablePagination } from "components/paginations/TablePagination"
import { buildBlacklistColumns } from "components/risk/columns"
import { BLACKLIST_ACTIVE_OPTIONS, BLACKLIST_TYPE_OPTIONS } from "components/risk/constants"
import { BlacklistEntryModal } from "components/risk/settings/BlacklistEntryModal"
import { DataTable } from "components/tables/DataTable"

import { riskService } from "services/risk/risk.service"
import { BlacklistEntry, BlacklistFilters, BlacklistType } from "services/risk/types"

export const BlacklistTab = () => {
  const [entries, setEntries] = useState<BlacklistEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [_total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  const [filters, setFilters] = useState<BlacklistFilters>({
    page: 1,
    pageSize: 20,
    q: "",
    type: undefined,
    is_active: undefined
  })

  const [modalOpen, setModalOpen] = useState(false)
  const [editingEntry, setEditingEntry] = useState<BlacklistEntry | null>(null)

  const updateFilter = <K extends keyof BlacklistFilters>(field: K, value: BlacklistFilters[K]) =>
    setFilters((f) => ({ ...f, [field]: value, page: 1 }))

  const loadEntries = useCallback(async () => {
    setLoading(true)
    try {
      const res = await riskService.getBlacklistEntries(filters)
      if (res) {
        setEntries(res.data)
        setTotal(res.total)
        setTotalPages(res.total_pages)
      }
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadEntries()
  }, [loadEntries])

  const openCreate = useCallback(() => {
    setEditingEntry(null)
    setModalOpen(true)
  }, [])

  const openEdit = useCallback((entry: BlacklistEntry) => {
    setEditingEntry(entry)
    setModalOpen(true)
  }, [])

  const handleToggleActive = useCallback(
    async (entry: BlacklistEntry) => {
      await riskService.updateBlacklistEntry(entry.id, { is_active: !entry.is_active } as Partial<BlacklistEntry>)
      await loadEntries()
    },
    [loadEntries]
  )

  const handleDelete = useCallback(
    async (entry: BlacklistEntry) => {
      if (!window.confirm(`Delete blacklist entry "${entry.value}"?`)) return
      await riskService.deleteBlacklistEntry(entry.id)
      toast.success("Entry deleted")
      await loadEntries()
    },
    [loadEntries]
  )

  const columns = useMemo(
    () => buildBlacklistColumns({ onEdit: openEdit, onToggle: handleToggleActive, onDelete: handleDelete }),
    [openEdit, handleToggleActive, handleDelete]
  )

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Blacklist Management</h2>
          <p className="text-sm opacity-60">
            Manage blacklisted emails, names, IPs, payment methods, and devices. Any player matching an active entry scores 100 on the
            blacklist risk factor.
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={openCreate}>
          + Add Entry
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="w-60">
          <InputField
            placeholder="Search value or reason..."
            value={filters.q}
            onChange={(e: ChangeEvent<HTMLInputElement>) => updateFilter("q", e.target.value)}
          />
        </div>
        <div className="w-40">
          <DropdownMenu
            placeholder="All Types"
            options={BLACKLIST_TYPE_OPTIONS}
            value={filters.type || ""}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              updateFilter("type", (e.target.value || undefined) as BlacklistType | undefined)
            }
          />
        </div>
        <div className="w-40">
          <DropdownMenu
            placeholder="All Status"
            options={BLACKLIST_ACTIVE_OPTIONS}
            value={filters.is_active ?? ""}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => updateFilter("is_active", e.target.value || undefined)}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center p-8">
          <span className="loading loading-spinner loading-lg" />
        </div>
      ) : entries.length === 0 ? (
        <div className="bg-white dark:bg-[#131421] border border-gray-200 dark:border-[rgba(255,255,255,0.1)] shadow-sm dark:shadow-none rounded-lg p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">No blacklist entries found</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-[#131421] border border-gray-200 dark:border-[rgba(255,255,255,0.1)] shadow-sm dark:shadow-none rounded-lg">
          <DataTable rows={entries} columns={columns} rowKey={(e) => e.id} />
        </div>
      )}

      {totalPages > 1 && (
        <TablePagination page={filters.page} totalPages={totalPages} onJump={(p) => setFilters((f) => ({ ...f, page: p }))} />
      )}

      <BlacklistEntryModal entry={editingEntry} open={modalOpen} onClose={() => setModalOpen(false)} onSave={loadEntries} />
    </div>
  )
}
